import React, {ReactElement, useEffect, useRef} from "react"
import {useAccessToken} from "../../services/auth"
import {
	createTheme,
	createWhiteLabelAssetUploadLink,
	getDefaultTheme,
	uploadWhiteLabelAsset,
	WhiteLabelTheme,
} from "../../resources/whiteLabel"
import * as orgSettingsResource from "../../resources/orgSettings"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {useToasts} from "react-toast-notifications"
import {OkDocument} from "../../resources/common"
import {useNavigate} from "react-router-dom"

export default function ReadyToLaunchEditorPage(): ReactElement {
	const accessToken = useAccessToken()
	const iframeRef = useRef<HTMLIFrameElement>(null)
	const {addToast} = useToasts()
	const navigate = useNavigate()

	const defaultThemeResult = useAsyncResult(() => getDefaultTheme(accessToken), [accessToken])

	const createThemeFunction = (themeData: OkDocument<WhiteLabelTheme>) => {
		const timestamp = new Date().getTime()
		const themeName = `editor-theme-${timestamp}`

		const {data} = themeData

		return createTheme(accessToken, {
			name: themeName,
			...data,
		})
	}

	const getOrgSettingsFunction = () => {
		return orgSettingsResource.getByToken(accessToken)
	}

	const updateOrgSettingsFunction = (settings: orgSettingsResource.OrgSettings, newThemeId: string) => {
		// We're going to update the whiteLabelDefaultThemeId, in order to do this we need to patch statementSettings (inside orgSettings)
		const patch = {
			statementsSettings: {
				...settings.attributes.statementsSettings,
				whiteLabelDefaultThemeId: newThemeId,
			},
		}

		return orgSettingsResource.update(accessToken, patch)
	}

	const [createThemeState, executeCreateTheme] = useAsyncResultIdle(createThemeFunction)
	const [getOrgSettingsState, executeGetOrgSettings] = useAsyncResultIdle(getOrgSettingsFunction)
	const [updateOrgSettingsState, executeUpdateOrgSettings] = useAsyncResultIdle(updateOrgSettingsFunction)
	const [, executeCreateWhiteLabelAssetUploadLink] = useAsyncResultIdle(createWhiteLabelAssetUploadLink)

	const handleIframeLoad = () => {
		// When the iframe loads, we're going to send the default theme data to the iframe
		defaultThemeResult.match(
			() => null,
			(themeData) => {
				const {attributes} = themeData

				if (iframeRef.current && iframeRef.current.contentWindow) {
					iframeRef.current.contentWindow.postMessage(
						{
							type: "LOAD_SETTINGS",
							data: attributes,
						},
						"*"
					)
				}
			},
			(error) => {
				console.error("Error fetching default theme:", error)
			}
		)
	}

	useEffect(() => {
		// When the iframe sends a SAVE_SETTINGS message, we're going to create a new theme, and update the org settings with the new default theme id
		const handleMessage = async (event: MessageEvent) => {
			if (event.data && event.data.type === "SAVE_SETTINGS") {
				console.log("Received SAVE_SETTINGS message:", event.data)
				await handleSaveSettings(event.data)
			} else if (event.data && event.data.type === "BACK_BUTTON_CLICKED") {
				console.log("Received BACK_BUTTON_CLICKED message")
				// Navigate to homepage
				navigate("/")
			}
		}

		window.addEventListener("message", handleMessage)

		return () => {
			window.removeEventListener("message", handleMessage)
		}
	}, [executeCreateTheme, accessToken, addToast, navigate])

	// Helper function to upload a file and get the download link
	const uploadFileAndGetLink = async (file: string, fileType: string): Promise<string | null> => {
		try {
			const uploadLinkResult = await executeCreateWhiteLabelAssetUploadLink(accessToken)

			return await uploadLinkResult.match<Promise<string | null>>(
				async (success) => {
					const {uploadLink, downloadLink} = success.data.attributes

					try {
						await uploadWhiteLabelAsset(uploadLink, file)
						return downloadLink
					} catch (error) {
						console.error(`Error uploading ${fileType} file:`, error)
						addToast(`Error uploading ${fileType} file`, {appearance: "error"})
						return null
					}
				},
				async (error) => {
					console.error(`Error creating ${fileType} upload link:`, error)
					addToast(`Error creating ${fileType} upload link`, {appearance: "error"})
					return null
				}
			)
		} catch (error) {
			console.error(`Error in ${fileType} upload process:`, error)
			return null
		}
	}

	// Handle saving settings and uploading files
	const handleSaveSettings = async (data: any) => {
		// Initialize updatedTheme with the original theme data
		let updatedTheme = {...data.data.theme}
		const hasLogoFile = !!data.data.logoFile
		const hasCardDesignFile = !!data.data.cardDesignFile

		try {
			// Process files in parallel if they exist
			const uploadPromises: Promise<void>[] = []

			if (hasLogoFile) {
				uploadPromises.push(
					(async () => {
						const logoDownloadLink = await uploadFileAndGetLink(data.data.logoFile, "Logo")
						if (logoDownloadLink) {
							updatedTheme = {
								...updatedTheme,
								global: {
									...updatedTheme.global,
									logoUrl: logoDownloadLink,
								},
							}
						}
					})()
				)
			}

			if (hasCardDesignFile) {
				uploadPromises.push(
					(async () => {
						const cardDesignDownloadLink = await uploadFileAndGetLink(data.data.cardDesignFile, "Card design")
						if (cardDesignDownloadLink) {
							const elementsCard = updatedTheme.elementsCard || {}
							const designs = elementsCard.designs || [{}]

							updatedTheme = {
								...updatedTheme,
								elementsCard: {
									...elementsCard,
									designs: [
										{
											...designs[0],
											url: cardDesignDownloadLink,
										},
										...designs.slice(1), // Keep any other designs if they exist
									],
								},
							}
						}
					})()
				)
			}

			// Wait for all uploads to complete
			if (uploadPromises.length > 0) {
				await Promise.all(uploadPromises)
			}

			// Create the theme with all updates
			executeCreateTheme({data: updatedTheme})
		} catch (error) {
			console.error("Error handling save settings:", error)
			// Fall back to creating theme without file uploads
			executeCreateTheme({data: data.data.theme})
		}
	}

	useEffect(() => {
		createThemeState.match(
			() => null,
			() => null,
			() => {
				executeGetOrgSettings()
			},
			(themeError) => {
				console.error("Error creating theme:", themeError)
				addToast("Error creating theme", {appearance: "error"})
			}
		)
	}, [createThemeState, executeGetOrgSettings, addToast])

	useEffect(() => {
		getOrgSettingsState.match(
			() => null,
			() => null,
			(orgSettingsSuccess) => {
				createThemeState.match(
					() => null,
					() => null,
					(themeSuccess) => {
						const createdThemeId = themeSuccess.data.id
						executeUpdateOrgSettings(orgSettingsSuccess, createdThemeId)
					},
					() => null
				)
			},
			(orgSettingsError) => {
				console.error("Error fetching org settings:", orgSettingsError)
				addToast("Error fetching organization settings", {appearance: "error"})
			}
		)
	}, [getOrgSettingsState, createThemeState, executeUpdateOrgSettings, addToast])

	useEffect(() => {
		updateOrgSettingsState.match(
			() => null,
			() => null,
			() => {
				addToast("Theme Saved Successfully", {appearance: "success"})
			},
			(updateError) => {
				console.error("Error updating org settings:", updateError)
				addToast("Error saving theme settings", {appearance: "error"})
			}
		)
	}, [updateOrgSettingsState, addToast])

	return (
		<div className="ready-to-launch-editor">
			<iframe
				ref={iframeRef}
				// src="http://localhost:3001/ready-to-launch/banking?mode=dashboard-editor"
				src="https://demo.unit.co/ready-to-launch/banking?mode=dashboard-editor"
				onLoad={handleIframeLoad}
			/>
		</div>
	)
}
